<template>
    <div class="flex w-full flex-col gap-4" :class="isFullscreen ? 'm-auto max-w-[480px]' : 'flex-1'">
        <div v-if="!isSessionComplete" class="flex items-center justify-between">
            <span class="text-[13px] font-semibold text-theme-text" role="status" aria-live="polite">{{
                progressText
            }}</span>
            <button
                type="button"
                class="flex items-center gap-1 rounded-md border border-theme-accent px-2 py-1 text-xs text-theme-accent transition-colors hover:bg-theme-accent hover:text-theme-accent-on focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                @click="endStudyMode"
            >
                {{ localizations[lang].flashcard.endStudy }}
            </button>
        </div>

        <FlashcardStudyResults v-if="isSessionComplete" />

        <template v-else>
            <div
                v-if="currentStudyCard"
                class="flex flex-col items-center"
                :class="{ 'min-h-0 flex-1 justify-center': onlyMobileVisible }"
            >
                <FlashcardCard
                    :card="currentStudyCard"
                    :is-flipped="flashcardStudyMode.isFlipped"
                    :size="isFullscreen ? 'fullscreen' : 'default'"
                    @flip="flipFlashcard"
                />

                <button
                    v-if="hasSource"
                    type="button"
                    class="mt-2 rounded-md px-2 py-1 text-xs text-theme-toolbar-icon opacity-70 transition-colors hover:bg-theme-accent-bg hover:text-theme-text hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                    @click="navigateToSourceFromStudy"
                >
                    {{ sourceLabel }}
                </button>

                <div v-if="flashcardStudyMode.isFlipped" class="mt-4 flex w-full gap-3">
                    <button
                        type="button"
                        :class="rateButtonClass('dontKnow')"
                        :aria-pressed="String(currentRating === 'dontKnow')"
                        @click="rate('dontKnow')"
                    >
                        <svg-icon icon="face-frown-24px" width="18px" height="18px" />
                        <span>{{ localizations[lang].flashcard.dontKnowIt }}</span>
                    </button>
                    <button
                        type="button"
                        :class="rateButtonClass('know')"
                        :aria-pressed="String(currentRating === 'know')"
                        @click="rate('know')"
                    >
                        <svg-icon icon="face-smile-24px" width="18px" height="18px" />
                        <span>{{ localizations[lang].flashcard.knowIt }}</span>
                    </button>
                </div>
            </div>

            <div class="flex items-center justify-between gap-2">
                <div class="flex items-center justify-center gap-3">
                    <PillIconButton
                        v-tooltip.top="localizations[lang].flashcard.shuffle"
                        icon="shuffle-24px"
                        :aria-label="localizations[lang].flashcard.shuffle"
                        variant="muted"
                        @click="reshuffleCards"
                    />
                    <PillIconButton
                        v-tooltip.top="localizations[lang].tooltips.prev"
                        icon="chevron-left"
                        :disabled="studyProgress.current <= 1"
                        :aria-label="localizations[lang].tooltips.prev"
                        variant="muted"
                        @click="prevStudyCard"
                    />
                    <FlashcardStudyProgress
                        :cards="flashcardStudyMode.cards"
                        :current-index="flashcardStudyMode.currentIndex"
                        :ratings="flashcardStudyMode.ratings"
                        :is-fullscreen="isFullscreen"
                    />
                    <PillIconButton
                        v-tooltip.top="localizations[lang].tooltips.next"
                        icon="chevron-right"
                        :disabled="studyProgress.current >= studyProgress.total"
                        :aria-label="localizations[lang].tooltips.next"
                        variant="muted"
                        @click="nextStudyCard"
                    />
                </div>

                <PillIconButton
                    v-if="!onlyMobileVisible"
                    v-tooltip.top="fullscreenTitle"
                    :icon="isFullscreen ? 'arrows-pointing-in-24px' : 'arrows-pointing-out-24px'"
                    :aria-label="fullscreenTitle"
                    :aria-pressed="String(isFullscreen)"
                    variant="muted"
                    @click="toggleStudyFullscreen"
                />
            </div>
        </template>
    </div>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import FlashcardCard from './FlashcardCard.vue';
import FlashcardStudyProgress from './FlashcardStudyProgress.vue';
import FlashcardStudyResults from './FlashcardStudyResults.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import flashcardSourceMixin from '../../js/mixins/flashcardSourceMixin';

const RATE_BTN_BASE =
    'study-rate-btn inline-flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2';

export default {
    name: 'FlashcardStudyMode',
    components: { FlashcardCard, FlashcardStudyProgress, FlashcardStudyResults, PillIconButton },
    mixins: [flashcardSourceMixin],
    computed: {
        ...mapState(['flashcardStudyMode', 'localizations', 'lang']),
        ...mapGetters(['currentStudyCard', 'studyProgress', 'studyResults', 'isEpub', 'isPdf', 'onlyMobileVisible']),
        /**
         * @returns {boolean} Whether the study panel is in fullscreen view mode
         */
        isFullscreen() {
            return this.flashcardStudyMode.isFullscreen;
        },
        /**
         * @returns {string} Tooltip for the fullscreen toggle, reflecting its next state
         */
        fullscreenTitle() {
            const flashcard = this.localizations[this.lang].flashcard;
            return this.isFullscreen ? flashcard.exitFullscreen : flashcard.fullscreen;
        },
        /**
         * @returns {string} Localized "{current} of {total}" progress text
         */
        progressText() {
            return this.localizations[this.lang].flashcard.progress
                .replace('{current}', this.studyProgress.current)
                .replace('{total}', this.studyProgress.total);
        },
        /**
         * @returns {Object} The flashcard the source-navigation mixin operates on
         */
        sourceCard() {
            return this.currentStudyCard;
        },
        /**
         * @returns {'know'|'dontKnow'|undefined} The current card's per-session rating
         */
        currentRating() {
            return this.flashcardStudyMode.ratings[this.currentStudyCard?.id];
        },
        /**
         * @returns {boolean} Whether every card in the session has been self-assessed
         */
        isSessionComplete() {
            const { know, dontKnow, total } = this.studyResults;
            return total > 0 && know + dontKnow >= total;
        },
    },
    methods: {
        ...mapMutations(['flipFlashcard', 'nextStudyCard', 'prevStudyCard', 'toggleStudyFullscreen']),
        ...mapActions([
            'endStudyMode',
            'startStudyMode',
            'rateFlashcard',
            'goToPage',
            'goToLocation',
            'toggleFlashcardsPanel',
        ]),
        reshuffleCards() {
            this.startStudyMode({ shuffle: true });
        },
        /**
         * Records the current card's self-assessment and advances to the next card,
         * unless it is the last card (the summary then takes over).
         * @param {'know'|'dontKnow'} rating - User's self-assessment for the current card
         */
        rate(rating) {
            this.rateFlashcard({ id: this.currentStudyCard.id, rating });
            if (this.studyProgress.current < this.studyProgress.total) {
                this.nextStudyCard();
            }
        },
        /**
         * Class binding for a rate button: shared base + the rating's color modifier + selected state.
         * @param {'know'|'dontKnow'} rating - Which rate button this is
         * @returns {Array} Vue :class binding
         */
        rateButtonClass(rating) {
            const modifier = rating === 'know' ? 'study-rate-btn--know' : 'study-rate-btn--dont-know';
            return [RATE_BTN_BASE, modifier, { 'is-selected': this.currentRating === rating }];
        },
        /**
         * Exits the fullscreen study layout (when active) before navigating to the card's source
         * position, so the navigated-to location is not left hidden behind the fullscreen panel.
         */
        navigateToSourceFromStudy() {
            if (this.isFullscreen) this.toggleStudyFullscreen();
            this.navigateToSource();
        },
    },
};
</script>

<style scoped>
/* Layout/spacing live in Tailwind utilities on the button; this block owns only the
   theme-var color states (hover / is-selected) that utilities can't cleanly express. */
.study-rate-btn {
    background: var(--theme-background-color);
    border: 1px solid;
    transition:
        background 0.15s,
        border-color 0.15s,
        color 0.15s;
}

/* "Don't know it": neutral grey outline; grey fill only on hover/selected. */
.study-rate-btn--dont-know {
    color: var(--theme-text-color);
    border-color: var(--theme-border-color);
}

.study-rate-btn--dont-know:hover,
.study-rate-btn--dont-know.is-selected {
    background: var(--theme-icons-background);
    border-color: var(--theme-text-color);
}

/* "Know it": green outline; green fill only on hover/selected. Colors centralized in
   _theme_ui.scss (--flashcard-*) so they match the dots/bar/pips on every theme. */
.study-rate-btn--know {
    color: var(--flashcard-known-strong);
    border-color: var(--flashcard-known);
}

.study-rate-btn--know:hover,
.study-rate-btn--know.is-selected {
    background: var(--flashcard-known-soft);
    border-color: var(--flashcard-known);
    color: var(--flashcard-known-strong);
}
</style>
